import React from 'react';
import PropTypes from 'prop-types';
import SVG from 'react-inlinesvg';
import Auth from 'modules/Auth';
import { PermissionsEnum } from 'consts/permissions';
import Table from 'lib/Table/Table';
import * as roleActions from 'modules/api/role';
import Modal, { ModalSize } from 'lib/Modal';
import withCancelRequest from 'components/hocs/withCancelRequest';
import Button, { Color, Size } from 'lib/Button';
import Card from 'components/common/Card';
import { CardTheme, CardType } from 'components/common/Card/Card.types';
import Search from 'lib/Search';
import plusSvg from 'assets/icons/plus.svg';
import filterSvg from 'assets/icons/filter.svg';
import editSvg from 'assets/icons/edit.svg';
import removeSvg from 'assets/icons/remove.svg';
import briefcaseSvg from 'assets/icons/briefcase.svg';
import Role from '../Role';
import { ActionRenderer, ViewRenderer } from '../GridCellRenderer';

class Roles extends React.Component {
  roleTableApi;

  setRoleGridApi = (tableApi) => {
    this.roleTableApi = tableApi;
  };

  isExternalFilterPresent = () => {
    const { filterRoleText } = this.state;
    return !!filterRoleText;
  };

  externalFilterPass = (node) => {
    const { filterRoleText } = this.state;
    if (filterRoleText) {
      return node.data.name.toLowerCase().includes(filterRoleText.toLowerCase());
    }
    return true;
  };

  onEditRole = (role) => {
    this.setState({
      selectedRole: role,
      isEditRoleDialogActive: true,
    });
  };

  onDeleteRole = (role) => {
    this.setState({
      selectedRole: role,
      isDeleteRoleDialogActive: true,
    });
  };

  onConfirmDeleteRole = async (role) => {
    const { cancelFunctions, addToastNotification } = this.props;
    try {
      await roleActions.deleteRole(cancelFunctions, role.id);
      addToastNotification({ description: `Role deleted successfully.`, type: 'Success', timeout: 2000 });
      this.getRoleList();

      this.setState({
        isDeleteRoleDialogActive: false,
      });
      // eslint-disable-next-line no-empty
    } catch {}
  };

  columnDefs = [
    {
      headerName: '',
      field: 'userId',
      minWidth: 70,
      maxWidth: 70,
      headerTooltip: 'Action',
      resizable: false,
      cellRenderer: ActionRenderer,
      cellRendererParams: {
        onClick: this.onEditRole,
        icon: editSvg,
        hasPermission: Auth.hasPermission(PermissionsEnum.ROLES_EDIT),
      },
      hide: !Auth.hasPermission(PermissionsEnum.ROLES_EDIT),
    },
    {
      headerName: 'Role',
      field: 'name',
      minWidth: 100,
      maxWidth: 200,
      headerTooltip: 'Role',
    },
    {
      headerName: 'Permissions',
      cellRenderer: ViewRenderer,
      field: 'permissions',
      minWidth: 200,
      headerTooltip: 'Permissions',
      cellRendererParams: {
        textKey: 'name',
      },
    },
    {
      headerName: 'Description',
      field: 'description',
      minWidth: 150,
      maxWidth: 200,
      headerTooltip: 'Description',
      resizable: false,
    },
    {
      headerName: '',
      field: 'userId',
      minWidth: 70,
      maxWidth: 70,
      headerTooltip: 'Action',
      resizable: false,
      cellRenderer: ActionRenderer,
      cellRendererParams: {
        onClick: this.onDeleteRole,
        icon: removeSvg,
        hasPermission: Auth.hasPermission(PermissionsEnum.ROLES_DELETE),
      },
      hide: !Auth.hasPermission(PermissionsEnum.ROLES_DELETE),
    },
  ];

  state = {
    filterRoleText: '',
    roleList: [],
    isEditRoleDialogActive: false,
    isDeleteRoleDialogActive: false,
    selectedRole: {
      name: '',
    },
  };

  async componentDidMount() {
    this.getRoleList();
  }

  async getRoleList() {
    const { cancelFunctions } = this.props;
    try {
      const roleList = await roleActions.getRoleList(cancelFunctions);
      const permissionList = await roleActions.getPermissionList(cancelFunctions);
      const mappedRoleList = this.mapRolePermission(roleList, permissionList);
      this.setState({ roleList: mappedRoleList });
      // eslint-disable-next-line no-empty
    } catch {}
  }

  mapRolePermission = (roleList, permissionList) => {
    return roleList.map((role) => {
      const tempRole = { ...role };
      tempRole.permissions = permissionList.filter((permission) => role.permissions.includes(permission.id));
      return tempRole;
    });
  };

  onCreateRole = () => {
    this.setState({
      selectedRole: {
        name: '',
      },
      isEditRoleDialogActive: true,
    });
  };

  onSaveRole = async (role) => {
    const { cancelFunctions, addToastNotification } = this.props;
    try {
      if (this.isRoleValid(role)) {
        if (role.id) {
          // update role
          await roleActions.updateRole(cancelFunctions, role);
          addToastNotification({
            description: 'Role updated successfully.',
            type: 'Success',
            timeout: 2000,
          });
        } else {
          // create roles
          await roleActions.createRole(cancelFunctions, role);
          addToastNotification({
            description: 'Role created successfully.',
            type: 'Success',
            timeout: 2000,
          });
        }
        this.setState({
          isEditRoleDialogActive: false,
        });
        this.getRoleList();
      } else {
        addToastNotification({
          description: 'Please Enter Mandatory Details.',
          type: 'Failure',
        });
      }
      // eslint-disable-next-line no-empty
    } catch {}
  };

  isRoleValid = (role) => {
    let isValid = false;
    if (role && role.name && role.description) {
      isValid = true;
    }
    return isValid;
  };

  handleDialogToggle = (dialogName) => {
    const isDialogActive = this.state[dialogName];
    this.setState({
      [dialogName]: !isDialogActive,
    });
  };

  onFilterTextChange = (value) => {
    this.setState({ filterRoleText: String(value ?? '') }, () => {
      this.roleTableApi.onFilterChanged();
    });
  };

  render() {
    const { roleList, filterRoleText, isEditRoleDialogActive, isDeleteRoleDialogActive, selectedRole } = this.state;

    return (
      <>
        <Modal
          isOpen={isEditRoleDialogActive}
          icon={briefcaseSvg}
          title={selectedRole.name ? 'Update role' : 'Create new role'}
          size={ModalSize.MEDIUM}
        >
          <Role
            role={selectedRole}
            onSave={this.onSaveRole}
            onCancel={() => {
              this.handleDialogToggle('isEditRoleDialogActive');
            }}
          />
        </Modal>
        <Modal
          isOpen={isDeleteRoleDialogActive}
          icon={removeSvg}
          title="Delete Role?"
          actionButtons={
            <>
              <Button
                color={Color.PRIMARY}
                onClick={() => {
                  this.handleDialogToggle('isDeleteRoleDialogActive');
                }}
                size={Size.LARGE}
                label="Cancel"
              />
              <Button
                color={Color.DANGER}
                onClick={() => {
                  this.onConfirmDeleteRole(selectedRole);
                }}
                size={Size.LARGE}
                label="Delete"
              />
            </>
          }
        >
          <p className="ml-8 body-base text-neutral-950-opacity-60">
            Are you sure you want to delete <strong>{selectedRole.name}</strong>?
          </p>
        </Modal>
        <Card cardType={CardType.FILTERS} cardTheme={CardTheme.WHITE}>
          <div className="row flex items-center">
            <div className="col-1">
              <div className="horizontal-vertical-center">
                <SVG className="fill-neutral-400 w-5 h-5" src={filterSvg} />
              </div>
            </div>
            <div className="col-9">
              <Search
                value={filterRoleText}
                onChange={this.onFilterTextChange}
                placeholder="Search role (type 3 letters)"
              />
            </div>
            <div className="col-12 col-md-2 flex justify-center">
              {Auth.hasPermission(PermissionsEnum.ROLES_CREATE) && (
                <Button
                  onClick={this.onCreateRole}
                  color={Color.PRIMARY}
                  size={Size.MEDIUM}
                  svg={plusSvg}
                  label="Create role"
                />
              )}
            </div>
          </div>
        </Card>
        <div className="row">
          <div className="col-12">
            <Table
              columnDefs={this.columnDefs}
              rowData={roleList}
              extraGridOptions={{
                isExternalFilterPresent: this.isExternalFilterPresent,
                doesExternalFilterPass: this.externalFilterPass,
                suppressScrollOnNewData: true,
                enableCellTextSelection: true,
              }}
              getTableApi={this.setRoleGridApi}
            />
          </div>
        </div>
      </>
    );
  }
}

Roles.propTypes = {
  cancelFunctions: PropTypes.objectOf(PropTypes.func).isRequired,
  addToastNotification: PropTypes.func.isRequired,
};

export default withCancelRequest(Roles);
